import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Scale, ShieldCheck, CreditCard, AlertCircle, FileText, Users, Ban, ChevronRight } from 'lucide-react';
import { isNativePlatform } from '../lib/platform';

const sections = [
  {
    id: 'objet',
    icon: FileText,
    title: "Objet et champ d'application",
    content: [
      "Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre la société 10 Millions Investor, ci-après dénommée \"la Plateforme\", et toute personne physique ou morale, ci-après dénommée \"le Membre\", souscrivant à l'un des abonnements proposés.",
      "L'accès et l'utilisation de la Plateforme impliquent l'acceptation sans réserve des présentes CGV. La Plateforme se réserve le droit de modifier les présentes CGV à tout moment. Les modifications prennent effet dès leur publication sur le site."
    ]
  },
  {
    id: 'services',
    icon: Users,
    title: "Description des services",
    content: [
      "10 Millions Investor est une plateforme communautaire dédiée à l'investissement immobilier en France. Elle propose :",
    ],
    list: [
      "Des outils de simulation et de calcul de rentabilité (Calculateur, Outil MDB)",
      "Un blog communautaire avec assistance IA pour la rédaction d'articles",
      "Un annuaire de professionnels partenaires (notaires, courtiers, agents immobiliers)",
      "Un espace communautaire d'échange entre investisseurs",
      "Un assistant IA spécialisé en investissement immobilier",
      "Des formations et contenus pédagogiques (Classroom)",
      "Un accès aux données cadastrales et DVF",
      "Un outil de diagnostic climatique immobilier"
    ]
  },
  {
    id: 'tarifs',
    icon: CreditCard,
    title: "Tarifs et modalités de paiement",
    content: [
      "La Plateforme propose deux formules d'abonnement :"
    ],
    list: [
      "Abonnement mensuel : 120\u20AC TTC / mois, sans engagement de durée",
      "Abonnement annuel : 1320\u20AC TTC / an (soit 2 mois offerts), engagement de 12 mois"
    ],
    extra: isNativePlatform() 
      ? "Des offres promotionnelles ponctuelles peuvent être proposées lors d'événements spéciaux. Les prix sont indiqués en euros TTC. Le paiement est géré via l'App Store (achat intégré). L'abonnement est auto-renouvelable et peut être annulé à tout moment dans les réglages de votre appareil (Réglages > Apple ID > Abonnements)."
      : "Des offres promotionnelles ponctuelles peuvent être proposées lors d'événements spéciaux. Les prix sont indiqués en euros TTC. Le paiement est effectué par carte bancaire via notre prestataire sécurisé Square. La facturation est automatique et récurrente selon la formule choisie."
  },
  {
    id: 'retractation',
    icon: ShieldCheck,
    title: "Droit de rétractation",
    content: [
      "Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation ne peut être exercé pour les contrats de fourniture de contenu numérique non fourni sur un support matériel dont l'exécution a commencé avec l'accord préalable exprès du consommateur.",
      "Toutefois, 10 Millions Investor offre une garantie de satisfaction de 14 jours. Si vous n'êtes pas satisfait de votre abonnement, contactez-nous à bonjour@10millionsinvestor.com dans les 14 jours suivant votre inscription pour obtenir un remboursement intégral."
    ]
  },
  {
    id: 'resiliation',
    icon: Ban,
    title: "Résiliation",
    content: [
      "L'abonnement mensuel peut être résilié à tout moment depuis l'espace personnel du Membre. La résiliation prend effet à la fin de la période en cours.",
      "L'abonnement annuel peut être résilié à l'issue de la période d'engagement de 12 mois. La résiliation doit être notifiée au moins 30 jours avant la date de renouvellement.",
      "La Plateforme se réserve le droit de suspendre ou résilier l'accès d'un Membre en cas de non-respect des présentes CGV, de la Charte de Qualité, ou de tout comportement portant atteinte à la communauté."
    ]
  },
  {
    id: 'responsabilite',
    icon: AlertCircle,
    title: "Responsabilité et garanties",
    content: [
      "Les informations, outils de calcul et contenus disponibles sur la Plateforme sont fournis à titre informatif et éducatif. Ils ne constituent en aucun cas des conseils en investissement personnalisés au sens de la réglementation financière.",
      "10 Millions Investor ne saurait être tenue responsable des décisions d'investissement prises par les Membres sur la base des informations consultées sur la Plateforme. Chaque investissement comporte des risques et il est recommandé de consulter un conseiller financier agréé avant toute décision.",
      "Les résultats des simulations (calculateur, MDB) sont des estimations et ne garantissent pas les rendements futurs."
    ]
  },
  {
    id: 'propriete',
    icon: Scale,
    title: "Propriété intellectuelle",
    content: [
      "L'ensemble des contenus présents sur la Plateforme (textes, images, vidéos, outils, algorithmes, design) sont la propriété exclusive de 10 Millions Investor et sont protégés par le droit d'auteur et le droit de la propriété intellectuelle.",
      "Les articles publiés par les Membres sur le blog communautaire restent la propriété intellectuelle de leurs auteurs. En les publiant sur la Plateforme, les auteurs accordent à 10 Millions Investor une licence non exclusive de diffusion."
    ]
  },
  {
    id: 'litiges',
    icon: Scale,
    title: "Droit applicable et litiges",
    content: [
      "Les présentes CGV sont régies par le droit français. En cas de litige, les parties s'engagent à rechercher une solution amiable avant toute action judiciaire.",
      "À défaut de résolution amiable, tout litige sera soumis aux tribunaux compétents de Lyon."
    ]
  }
];

const CGVPage = () => {
  const [activeSection, setActiveSection] = useState('objet');
  const navigate = useNavigate();
  const _isNative = isNativePlatform();

  // Redirect to dashboard on mobile (Apple Guideline 3.1.1)
  useEffect(() => {
    if (_isNative) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate, _isNative]);

  useEffect(() => {
    if (_isNative) return; // skip scroll listener on mobile
    const handleScroll = () => {
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 140 && rect.bottom > 140) {
            setActiveSection(s.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [_isNative]);

  // CRITICAL: return null on mobile AFTER all hooks to prevent flash of pricing text
  // CGV contains "Abonnement mensuel/annuel" text that violates Apple Guideline 3.1.1
  if (_isNative) return null;

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="cgv-page">
      {/* Nav */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_4ed8f34b-1bc9-42f5-8577-4843189c4948/artifacts/jd7g0w5b_LOGO%2010MILLIONSINVESTOR%20blanc.png"
              alt="10M Investor" className="h-7"
            />
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 transition-colors" data-testid="cgv-back-link">
            <ChevronLeft className="w-4 h-4" />
            Retour
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(13,71,161,0.3)_0%,_transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(0,176,80,0.15)_0%,_transparent_60%)]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-slate-500 text-sm hover:text-slate-300 transition-colors">Accueil</Link>
            <ChevronRight className="w-3 h-3 text-slate-600" />
            <span className="text-[#00B050] text-sm font-medium">CGV</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-4">
            Conditions Générales<br />
            <span className="bg-gradient-to-r from-[#0D47A1] to-[#00B050] bg-clip-text text-transparent">de Vente</span>
          </h1>
          <p className="text-slate-400 max-w-xl">
            Tout ce que vous devez savoir sur nos conditions d'utilisation et de vente, en toute transparence.
          </p>
          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <div className="w-2 h-2 rounded-full bg-[#00B050] animate-pulse" />
              Dernière mise à jour : Mars 2026
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-[220px_1fr] gap-10">
          {/* Sidebar - Table of Contents */}
          <aside className="hidden lg:block">
            <div className="sticky top-20">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">Sommaire</p>
              <nav className="space-y-1">
                {sections.map((s, idx) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={(e) => { e.preventDefault(); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                      activeSection === s.id
                        ? 'bg-[#0D47A1]/10 text-[#0D47A1] font-semibold'
                        : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                    }`}
                    data-testid={`cgv-toc-${s.id}`}
                  >
                    <span className={`w-5 h-5 rounded-md text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${
                      activeSection === s.id ? 'bg-[#0D47A1] text-white' : 'bg-slate-200 text-slate-500'
                    }`}>{idx + 1}</span>
                    <span className="truncate">{s.title}</span>
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Sections */}
          <div className="space-y-6">
            {sections.map((section, idx) => (
              <section
                key={section.id}
                id={section.id}
                className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 hover:shadow-lg hover:shadow-slate-100/50 transition-all duration-300 scroll-mt-20"
                data-testid={`cgv-section-${section.id}`}
              >
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[#0D47A1]/5 flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-[#0D47A1]" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#0D47A1]/60 mb-1">Article {idx + 1}</p>
                    <h2 className="text-lg font-bold text-slate-900">{section.title}</h2>
                  </div>
                </div>
                <div className="pl-14 space-y-3">
                  {section.content.map((p, i) => (
                    <p key={i} className="text-slate-600 leading-relaxed text-[15px]">{p}</p>
                  ))}
                  {section.list && (
                    <ul className="space-y-2.5 mt-4">
                      {section.list.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-600 text-[15px]">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#00B050] mt-2 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.extra && (
                    <p className="text-slate-600 leading-relaxed mt-4 text-[15px]">{section.extra}</p>
                  )}
                </div>
              </section>
            ))}

            {/* CTA */}
            <div className="bg-slate-900 rounded-2xl p-8 sm:p-10 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(0,176,80,0.15)_0%,_transparent_70%)]" />
              <div className="relative">
                <p className="text-white/70 mb-2 text-sm">Une question sur nos CGV ?</p>
                <a href="mailto:bonjour@10millionsinvestor.com" className="text-white font-bold text-xl hover:text-[#00B050] transition-colors" data-testid="cgv-contact-email">
                  bonjour@10millionsinvestor.com
                </a>
                <div className="flex items-center justify-center gap-6 mt-6">
                  <Link to="/confidentialite" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1">
                    Confidentialité <ChevronRight className="w-3 h-3" />
                  </Link>
                  <Link to="/contact" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1">
                    Contact <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-6 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-2">
          <span>10 Millions Investor &copy; 2026</span>
          <div className="flex items-center gap-4">
            <Link to="/confidentialite" className="hover:text-slate-600 transition-colors">Confidentialité</Link>
            <Link to="/contact" className="hover:text-slate-600 transition-colors">Contact</Link>
            <Link to="/charte" className="hover:text-slate-600 transition-colors">Charte</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CGVPage;
